import { ChangeEvent, FC, KeyboardEvent, useEffect, useState } from 'react'

import { RenderIf } from 'components/RenderIf'
import { useMyDispatch } from 'featureSliced/shared/hooks/useMyDispatch'
import { useMySelector } from 'featureSliced/shared/hooks/useMySelector'
import { useTranslations } from 'featureSliced/shared/hooks/useTranslations'
import { FiltersSlice } from 'featureSliced/shared/store/reducers/FiltersReducer'
import { convertToPrice } from 'featureSliced/shared/utils/convertToPrice'

import clickAndCollectLogo from 'assets/prompt/clickAndCollect.svg'
import qrMenuLogo from 'assets/prompt/qrMenu.svg'
import basket from 'assets/basket.svg'
import filters from 'assets/settings.svg'
import search from 'assets/search.svg'
import menuIcon from 'assets/meatballs.svg'

import {
  WebAction,
  WebActionLabel,
  WebActions,
  WebCartAction,
  WebHeaderBar,
  WebHeaderSpacer,
  WebLogo,
  WebSearch,
  WebSearchInput,
} from './styled'

type WebHeaderProps = {
  openAllergensModal: () => void
  openSidebar: () => void
  /** total of the local cart, already formatted; null hides the amount */
  cartTotal: string | null
  cartCount: number
  onCartClick: () => void
  /** the cart lives in its own column on wide screens, so the header drops it there */
  isCartInHeader: boolean
}

export const WebHeader: FC<WebHeaderProps> = ({
  openAllergensModal,
  openSidebar,
  cartTotal,
  cartCount,
  onCartClick,
  isCartInHeader,
}) => {
  const t = useTranslations()
  const dispatch = useMyDispatch()

  const { addSearch, discardSearch } = FiltersSlice.actions
  const storedSearch = useMySelector((state) => state.filters.search)
  const { logoImage } = useMySelector((state) => state.app.interfaceStyles)

  const [value, setValue] = useState(storedSearch || '')

  useEffect(() => {
    setValue(storedSearch || '')
  }, [storedSearch])

  const submit = () => {
    if (value) {
      dispatch(addSearch(value))
    } else {
      dispatch(discardSearch())
    }
  }

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') submit()
  }

  const fallbackLogo =
    process.env.REACT_APP_IS_CLIC === '1' ? clickAndCollectLogo : qrMenuLogo

  return (
    <WebHeaderBar data-testid="web-header">
      <WebLogo src={logoImage || fallbackLogo} alt="" />

      <WebSearch>
        <img src={search} alt="" width="18" height="18" />
        <WebSearchInput
          value={value}
          placeholder={t('footer.search')}
          onChange={(e: ChangeEvent<HTMLInputElement>) =>
            setValue(e.target.value)
          }
          onBlur={submit}
          onKeyDown={handleKeyDown}
          aria-label={t('footer.search')}
        />
      </WebSearch>

      <WebHeaderSpacer />

      <WebActions>
        <WebAction type="button" onClick={openAllergensModal}>
          <img src={filters} alt="" width="18" height="18" />
          <WebActionLabel>{t('footer.filters')}</WebActionLabel>
        </WebAction>

        <RenderIf condition={isCartInHeader}>
          <WebCartAction type="button" onClick={onCartClick}>
            <img src={basket} alt="" width="18" height="14" />
            <span>{convertToPrice(cartCount)}</span>
            <RenderIf condition={!!cartTotal}>
              <WebActionLabel>{cartTotal}</WebActionLabel>
            </RenderIf>
          </WebCartAction>
        </RenderIf>

        {/* the burger is always the last icon on the right */}
        <WebAction
          type="button"
          onClick={openSidebar}
          aria-label={t('cover.menu')}
        >
          <img src={menuIcon} alt="" width="20" height="20" />
        </WebAction>
      </WebActions>
    </WebHeaderBar>
  )
}
